"use client";

import { useState } from "react";
import { Search, Filter } from "lucide-react";
import Table from "../table";
import styles from "../../styles/case-manager-modules/patientsTable.module.css";
import { useNavigate } from "react-router-dom";
import Tooltip from "../shared-modules/Tooltip";
import FilterPanel from "../shared-modules/FilterDrawer/FilterDrawer";
import { formatDob } from "../../utils/helper";
import { useStatuses } from "../../hooks/useMyTasksQueries";
import { formatLastCallDate } from "../../utils/dateFormatters";

// Module-scope helpers (extracted from PriorityTableSection — these don't
// depend on component state, so defining them per render was wasted work).

const ESCALATION_COLOR_RANGES = [
  { min: 0, max: 0, color: "#FFFFFF", label: "New patient (no escalation history)" },
  { min: 1, max: 30, color: "#ffee5891", label: "Occasional escalations" },
  { min: 31, max: 60, color: "#FFD699", label: "Frequent escalations" },
  { min: 61, max: 100, color: "#FFCCCC", label: "High-risk repeated escalations" },
];

const getRowColor = (patient) => {
  const freq = Number(patient?.escalated_readings_frequency);

  if (!Number.isNaN(freq)) {
    const match = ESCALATION_COLOR_RANGES.find(
      ({ min, max }) => freq >= min && freq <= max
    );

    if (match) {
      return match.color;
    }
  }

  if (
    patient.statuses &&
    patient.statuses.some(
      (status) =>
        status.status === "Need Call" || status.status === "Needs call"
    )
  ) {
    return "#8ECBF6";
  }

  if (
    patient.statuses &&
    patient.statuses.some(
      (status) =>
        status.status === "Call completed" ||
        status.status === "Finalized" ||
        status.status === "Call Completed"
    )
  ) {
    return "#E6F7FF";
  }

  if (patient.is_compliant) {
    return "#C3E3FA";
  }

  return "transparent";
};

const getRowClassName = (patient) => {
  const backgroundColor = getRowColor(patient);

  if (backgroundColor === "#0D1440") {
    return styles.dark_background_row;
  }

  return "";
};

const getLastReading = (reading) => {
  if (reading === null || reading === undefined) return "--";
  if (reading === 0) {
    return <span style={{ color: "#63c132" }}>Today</span>;
  }
  if (reading === 1) {
    return <span style={{ color: "#63c132" }}>Yesterday</span>;
  }
  return `${reading} days ago`;
};

const PriorityTableSection = ({
  patients = [],
  locationColumn = false,
  backURL,
  loading = false,
  isOutOfRangePatient = false,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const navigate = useNavigate();

  const { data: statuses = [], isLoading: statusesLoading } = useStatuses();

  const handleFilterClick = (statusId) => {
    if (activeFilters.includes(statusId)) {
      setActiveFilters(activeFilters.filter((id) => id !== statusId));
    } else {
      setActiveFilters([...activeFilters, statusId]);
    }
  };

  const handleClearFilters = () => {
    setActiveFilters([]);
  };

  const handleRowClick = (patient) => {
   
    sessionStorage.setItem("_pt_id", patient.id);
    const ccm = Number(patient.ccm);
    const rpm = Number(patient.rpm);

    if (window.location.pathname === "/my-tasks") {
      navigate(
        `/my-tasks/out-of-range/escalated-review?patient_id=${patient.id}`,
        {
          state: {
            backURL,
          },
        }
      );

      sessionStorage.setItem("_back_url", backURL);
    } else {
      navigate(
        `/admin-case-manager/escalated-review?patient_id=${patient.id}`,
        {
          state: {
            backURL,
          },
        }
      );
      sessionStorage.setItem("_back_url", "/admin-case-manager");
    }
  };

  const filteredPatients = patients?.filter((patient) => {
    const fullName =
      `${patient?.first_name} ${patient?.last_name}`.toLowerCase();
    const nameMatch = fullName.includes(searchQuery.toLowerCase());

    if (activeFilters.length > 0) {
      const selectedStatusNames = activeFilters
        .map((filterId) => {
          const status = statuses.find((s) => s?.id === filterId);
          return status ? status?.name : null;
        })
        .filter(Boolean);

      return (
        nameMatch &&
        patient.statuses &&
        patient.statuses.some((statusObj) => {
          return selectedStatusNames.includes(statusObj.status);
        })
      );
    }

    return nameMatch;
  });

  const columns = [
    {
      key: "name",
      label: "Name",
      sorting: true,
      alignLeft: true,
      render: (patient) => `${patient?.first_name} ${patient?.last_name}`,
    },
    {
      key: "statuses",
      label: "Statuses",
      alignLeft: true,
      render: (patient) => {
        if (!patient.statuses || patient.statuses.length === 0) {
          return "_ _";
        }

        return (
          <div className={styles.patient_status_container}>
            {patient.statuses.filter(
                (status) =>
                  status.status !== "voice-mail" &&
                  status.status !== "two-way-communication"
              ).map((status, index) => (
              <div key={index} className={styles.patient_status_item}>
                <span className={styles.filter_tab}>
                  {status.status}
                  {status.status === "voice-mail" && status.voicemail_count
                    ? ` (${status.voicemail_count}x)`
                    : ""}
                  {status.status === "two-way-communication" && status.TWC_count
                    ? ` (${status.TWC_count}x)`
                    : ""}
                  {status.description && (
                    <Tooltip content={status.description} position="right">
                      <span className={styles.status_icon}>?</span>
                    </Tooltip>
                  )}
                </span>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      key: "dob",
      label: "DOB",
      sorting: true,
      alignLeft: true,
      render: (patient) => formatDob(patient?.dob),
    },
    {
      key: "location_name",
      label: "Location",
      sorting: true,
      alignLeft: true,
      render: (patient) => patient?.location_name || patient?.location?.name || "_ _",
    },
    ...(locationColumn
      ? [
          {
            key: "location",
            label: "Location",
            sorting: false,
            render: (patient) => {
              const location = patient?.location;
              if (location && typeof location === "object") {
                return location.name || "_ _";
              }
              return location || "_ _";
            },
          },
        ]
      : []),
    {
      key: "provider",
      label: "Provider",
      sorting: true,
      alignLeft: true,
      render: (patient) => patient?.provider?.first_name || "_ _",
    },
    {
      key: "escalated_readings_frequency",
      label: "Escalated Readings Frequency",
      sorting: true,
      alignLeft: true,
      render: (patient) => patient?.escalated_readings_frequency != null
        ? `${patient.escalated_readings_frequency}%`
        : "--",
    
    },
   
    // {
    //   key: "priority_score",
    //   label: "Priority Score",
    //   sorting: true,
    //   render: (patient) => patient?.priority_score || "_ _",
    // },
   
    

    {
      key: "lastReading",
      label: "Latest Readings",
      sorting: true,
      alignLeft: true,
      render: (patient) => getLastReading(patient.days_since_last_reading),
    },
    {
      key: "latest_call",
      label: "Last Call",
      alignLeft: true,
      sorting: true,
      render: (row) => {
  if (!row.latest_call) {
    return "_ _";
  }

  const commStatus = row.statuses?.find(
    (s) =>
      s.status === "two-way-communication" ||
      s.status === "voice-mail"
  );

  let label = "";
  let count = "";
  let bgColor = "#eee";
  let textColor = "#333";

  if (commStatus?.status === "two-way-communication") {
    label = "Two-Way ";
    count = commStatus.TWC_count ? ` (${commStatus.TWC_count}x)` : "";
    bgColor = "#D4EEE0";
    textColor = "#1A5D3D";
  }

  if (commStatus?.status === "voice-mail") {
    label = "Voicemail";
    count = commStatus.voicemail_count
      ? ` (${commStatus.voicemail_count}x)`
      : "";
    bgColor = "#F9F1D9";
    textColor = "#7E6513";
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {label && (
        <span
          style={{
            backgroundColor: bgColor,
            color: textColor,
            padding: "4px 10px",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: 500,
            width: "fit-content",
          }}
        >
          {label}
          {count}
        </span>
      )}

      <span>{formatLastCallDate(row.latest_call)}</span>
    </div>
  );
},

    },
    {
      key: "program",
      label: "Program",
      sorting: true,
      render: (patient) =>
        patient?.rpm && patient?.ccm
          ? "RPM | CCM"
          : patient?.rpm
          ? "RPM"
          : patient?.ccm
          ? "CCM"
          : "None",
    },
  ];

  // hide specific columns when viewing the Out of Range Patients table
  const visibleColumns = isOutOfRangePatient
    ? columns.filter(
        (c) =>
          c.key !== "location_name" &&
          c.key !== "dob" &&
          c.key !== "escalated_readings_frequency"
      )
    : columns;

  return (
    <div className={styles.patients_section}>
      {/* Search and Filter Controls */}
      <div className={styles.search_container}>
        <div
          style={{
            display: "flex",
            gap: "12px",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <div className={styles.search_box}>
            <input
              type="text"
              className={styles.search_input}
              placeholder="Search Patients by Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search size={16} className={styles.search_icon} />
          </div>

          <button
            className={`${styles.filter_trigger_button} ${
              activeFilters.length > 0 ? styles.active : ""
            }`}
            onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
          >
            <Filter size={12} />
            Status Filters
            {activeFilters.length > 0 && (
              <span
                style={{
                  backgroundColor: "#DBEAF8",
                  color: "#10164D",
                  borderRadius: "10px",
                  padding: "2px 4px",
                  fontSize: "10px",
                  minWidth: "18px",
                  textAlign: "center",
                }}
              >
                {activeFilters.length}
              </span>
            )}
          </button>

          {activeFilters.length > 0 && (
            <button
              className={styles.clear_all_button}
              onClick={handleClearFilters}
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Inline Filter Panel */}
      <div className={styles.patient_status_container}>
        <FilterPanel
          isOpen={isFilterPanelOpen}
          onToggle={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
          statuses={statuses}
          activeFilters={activeFilters}
          onFilterChange={handleFilterClick}
          filteredData={filteredPatients}
          statusesLoading={statusesLoading}
        />
      </div>

      {/* Escalation Level Indicators */}
      <div
        style={{
          display: "flex",
          gap: "15px",
          alignItems: "center",
          marginBottom: "16px",
          padding: "8px 12px",
          backgroundColor: "#F9FAFB",
          borderRadius: "6px",
          flexWrap: "wrap",
          width: "fit-content",
         
        }}
      >
        <span
          style={{
            fontSize: "11px",
            fontWeight: 600,
            color: "#6B7280",
            marginRight: "4px",
          }}
        >
          Indicators:
        </span>
        {ESCALATION_COLOR_RANGES.map((range, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <div
              style={{
                width: "14px",
                height: "14px",
                backgroundColor: range.color,
                borderRadius: "3px",
                border: "1px solid #E5E7EB",
              }}
            />
            <span
              style={{
                fontSize: "11px",
                color: "#374151",
              }}
            >
              {range.label}
            </span>
          </div>
        ))}
      </div>

      <div style={{ height: "560px", overflow: "auto" }}>
        <Table
          columns={visibleColumns}
          data={filteredPatients}
          height={530}
          loading={loading}
          isRowClickable={true}
          onRowClick={handleRowClick}
          rowColor={getRowColor}
          getRowClassName={getRowClassName}
        />
      </div>
    </div>
  );
};

export default PriorityTableSection;
